import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import useScrollToTop from "../../hooks/useScollToTop";
import HeroImage from "../../components/Hero/HeroImage";
import {
  getAvailableLiveEvents,
  getEventLiveHub,
} from "../../services/eventLiveServices";
import {
  FaExternalLinkAlt,
  FaVideo,
  FaSyncAlt,
  FaQrcode,
  FaBroadcastTower,
} from "react-icons/fa";

/* =========================
   Helpers
   ========================= */

const normalize = (s = "") =>
  String(s || "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toUpperCase()
    .trim();

const isTapisLabel = (mat) => {
  const label =
    mat?.label || (mat?.mat_number ? `TAPIS ${mat.mat_number}` : "");
  return normalize(label).startsWith("TAPIS");
};

const isValidUrl = (value = "") => {
  try {
    const u = new URL(String(value).trim());
    return ["http:", "https:"].includes(u.protocol);
  } catch {
    return false;
  }
};

const fmtDate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("fr-FR");
};

const fmtRange = (start, end) => {
  const a = fmtDate(start);
  const b = fmtDate(end);
  if (a && b) return `${a} → ${b}`;
  return a || b || "—";
};

const slotPillClass = (label = "") => {
  const t = normalize(label);
  if (t.startsWith("M"))
    return "bg-sky-100 text-sky-900 ring-sky-200 dark:bg-sky-500/15 dark:text-sky-200 dark:ring-sky-400/20";
  if (t.startsWith("F"))
    return "bg-pink-100 text-pink-900 ring-pink-200 dark:bg-pink-500/15 dark:text-pink-200 dark:ring-pink-400/20";
  return "bg-slate-100 text-slate-900 ring-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:ring-slate-700";
};

/* =========================
   UI Components
   ========================= */

function EventCard({ ev, isSelected }) {
  const stats = ev?.stats || {};
  const badge = (txt) => (
    <span className="px-2 py-0.5 rounded-full text-xs bg-slate-100 ring-1 ring-slate-200 text-slate-700 dark:bg-slate-800 dark:ring-slate-700 dark:text-slate-200">
      {txt}
    </span>
  );

  return (
    <Link
      to={`/live-events/${ev.id}`}
      className={[
        "group rounded-2xl overflow-hidden bg-white dark:bg-slate-900/70 shadow-sm hover:shadow-md transition ring-1",
        isSelected
          ? "ring-indigo-500 ring-2"
          : "ring-slate-200 dark:ring-slate-800",
      ].join(" ")}
      title="Ouvrir cette compétition"
    >
      <div className="h-32 bg-slate-100 dark:bg-slate-800 overflow-hidden">
        <img
          src={ev.photo_url || "/img/no-image.jpg"}
          alt={ev.titre}
          className="w-full h-full object-cover group-hover:scale-[1.02] transition"
          onError={(e) => (e.currentTarget.src = "/img/no-image.jpg")}
        />
      </div>

      <div className="p-3">
        <div className="font-extrabold text-slate-900 dark:text-white line-clamp-2">
          {ev.titre}
        </div>
        <div className="text-xs text-slate-600 dark:text-slate-300 mt-1">
          {fmtRange(ev.start, ev.end)} • {ev.lieu_event || "—"}
        </div>

        <div className="mt-2 flex flex-wrap gap-1.5">
          {badge(`${stats.mats_total ?? 0} tapis`)}
          {badge(`${stats.mats_open ?? 0} ouverts`)}
          {badge(`${stats.mats_with_slots ?? 0} catégories`)}
          {badge(`${stats.mats_live ?? 0} live`)}
        </div>
      </div>
    </Link>
  );
}

function MatCard({ mat, isSelected, onSelect }) {
  const hasStream = Boolean(mat?.stream?.stream_url);
  const isLive = Boolean(mat?.stream?.is_live);

  const label =
    mat?.label || (mat?.mat_number ? `TAPIS ${mat.mat_number}` : "TAPIS");

  return (
    <button
      type="button"
      className={[
        "w-full text-left rounded-2xl p-3 sm:p-4 transition select-none ring-1 shadow-sm",
        "bg-yellow-300 ring-yellow-100/70 text-slate-900 hover:shadow-md",
        isSelected ? "ring-4 ring-indigo-500" : "",
      ].join(" ")}
      onClick={() => onSelect(mat)}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="text-lg sm:text-xl font-extrabold">{label}</div>

        {hasStream ? (
          <span
            className={[
              "inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-extrabold",
              isLive
                ? "bg-indigo-600 text-white"
                : "bg-slate-800 text-white",
            ].join(" ")}
          >
            {isLive ? "LIVE" : "VIDÉO"}
          </span>
        ) : (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-extrabold bg-white/70 text-slate-700 ring-1 ring-black/10">
            SANS LIVE
          </span>
        )}
      </div>

      {Array.isArray(mat?.slots) && mat.slots.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {mat.slots.slice(0, 3).map((s) => (
            <span
              key={s.id || s.slot_index}
              className={[
                "inline-flex items-center rounded-full px-3 py-1 text-sm font-extrabold ring-1",
                slotPillClass(s.label),
              ].join(" ")}
              title={s.note || ""}
            >
              {s.label}
            </span>
          ))}
        </div>
      ) : (
        <div className="mt-3 text-xs font-semibold text-slate-700/80">
          Aucune catégorie affichée
        </div>
      )}
    </button>
  );
}

function FollowCompetitionCard({ url }) {
  if (!isValidUrl(url)) return null;

  return (
    <div className="rounded-2xl ring-1 ring-slate-200 dark:ring-slate-800 bg-white dark:bg-slate-900/70 p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-2xl bg-indigo-600 text-white flex items-center justify-center">
          <FaQrcode />
        </div>
        <div>
          <div className="font-extrabold text-slate-900 dark:text-white">
            Suivi compétition
          </div>
          <div className="text-sm text-slate-600 dark:text-slate-300">
            Scan du QR code ou ouverture directe du lien.
          </div>
        </div>
      </div>

      <div className="mt-4 flex justify-center">
        <div className="rounded-2xl bg-white p-3 ring-1 ring-slate-200">
          <QRCodeSVG
            value={url}
            size={170}
            bgColor="#ffffff"
            fgColor="#111827"
            level="M"
            includeMargin={true}
          />
        </div>
      </div>

      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-white hover:bg-indigo-700 font-semibold"
      >
        Ouvrir le suivi compétition <FaExternalLinkAlt />
      </a>
    </div>
  );
}

function Player({ mat }) {
  const stream = mat?.stream;
  const isOpen = Boolean(mat?.is_open);
  const hasStream = Boolean(stream?.stream_url);
  const embed = stream?.embed_url;

  if (!mat) {
    return (
      <div className="rounded-2xl ring-1 ring-slate-200 dark:ring-slate-800 bg-white dark:bg-slate-900/70 p-6">
        <div className="text-lg font-semibold text-slate-900 dark:text-white">
          Sélectionnez un tapis
        </div>
        <div className="text-sm text-slate-600 dark:text-slate-300 mt-1">
          Choisissez un tapis ouvert pour afficher la vidéo ou les catégories.
        </div>
      </div>
    );
  }

  if (!isOpen) {
    return (
      <div className="rounded-2xl ring-1 ring-slate-200 dark:ring-slate-800 bg-white dark:bg-slate-900/70 p-6">
        <div className="text-lg font-semibold text-slate-900 dark:text-white">
          Tapis fermé
        </div>
        <div className="text-sm text-slate-600 dark:text-slate-300 mt-1">
          Ce tapis n’est pas actif pour le moment.
        </div>
      </div>
    );
  }

  if (!hasStream) {
    return (
      <div className="rounded-2xl ring-1 ring-slate-200 dark:ring-slate-800 bg-white dark:bg-slate-900/70 p-6">
        <div className="text-lg font-semibold text-slate-900 dark:text-white">
          Aucun live configuré
        </div>
        <div className="text-sm text-slate-600 dark:text-slate-300 mt-1">
          La répartition du tapis est visible, mais aucun flux vidéo n’a encore
          été renseigné.
        </div>
      </div>
    );
  }

  if (embed) {
    return (
      <div className="rounded-2xl overflow-hidden ring-1 ring-slate-200 dark:ring-slate-800 bg-black">
        <div className="aspect-video w-full">
          <iframe
            title={stream?.title || "Live"}
            src={embed}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl ring-1 ring-slate-200 dark:ring-slate-800 bg-white dark:bg-slate-900/70 p-6">
      <div className="text-lg font-semibold text-slate-900 dark:text-white">
        {stream?.title || "Live vidéo"}
      </div>
      <div className="text-sm text-slate-600 dark:text-slate-300 mt-1">
        Ce fournisseur ne supporte pas l’embed direct ici.
      </div>
      <a
        href={stream.stream_url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700"
      >
        Ouvrir le live <FaExternalLinkAlt />
      </a>
    </div>
  );
}

/* =========================
   Page
   ========================= */

export default function EventLivePublic() {
  useScrollToTop();

  const { eventId } = useParams();
  const navigate = useNavigate();

  const [available, setAvailable] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);

  const [selectedEventId, setSelectedEventId] = useState(null);
  const [hub, setHub] = useState(null);
  const [loadingHub, setLoadingHub] = useState(false);

  const [selectedMatNumber, setSelectedMatNumber] = useState(1);

  useEffect(() => {
    (async () => {
      setLoadingEvents(true);
      try {
        const list = await getAvailableLiveEvents();
        setAvailable(Array.isArray(list) ? list : []);
      } finally {
        setLoadingEvents(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (eventId) setSelectedEventId(Number(eventId));
  }, [eventId]);

  useEffect(() => {
    if (eventId) return;
    if (!selectedEventId && available.length) {
      setSelectedEventId(Number(available[0].id));
    }
  }, [eventId, selectedEventId, available]);

  const fetchHub = async (eid) => {
    if (!eid) return;
    setLoadingHub(true);
    try {
      const data = await getEventLiveHub(eid);
      setHub(data || null);

      const matsSorted = (data?.mats || [])
        .slice()
        .sort(
          (a, b) =>
            (a.sort_order ?? a.mat_number) - (b.sort_order ?? b.mat_number)
        );

      const visibleOpenMats = matsSorted.filter(
        (m) => isTapisLabel(m) && Boolean(m?.is_open)
      );

      const firstWithStream = visibleOpenMats.find((m) => m?.stream?.stream_url);
      const firstOpen = visibleOpenMats[0];

      setSelectedMatNumber(firstWithStream?.mat_number || firstOpen?.mat_number || 1);
    } finally {
      setLoadingHub(false);
    }
  };

  useEffect(() => {
    if (!selectedEventId) return;
    fetchHub(selectedEventId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEventId]);

  const mats = useMemo(() => {
    const arr = hub?.mats || [];
    return arr
      .slice()
      .sort(
        (a, b) =>
          (a.sort_order ?? a.mat_number) - (b.sort_order ?? b.mat_number)
      );
  }, [hub]);

  const matsOpenPlayable = useMemo(() => {
    return mats.filter((m) => isTapisLabel(m) && Boolean(m?.is_open));
  }, [mats]);

  const matsOpenWithStream = useMemo(() => {
    return matsOpenPlayable.filter((m) => Boolean(m?.stream?.stream_url));
  }, [matsOpenPlayable]);

  const selectedMat = useMemo(() => {
    return (
      matsOpenPlayable.find(
        (m) => Number(m.mat_number) === Number(selectedMatNumber)
      ) ||
      matsOpenPlayable[0] ||
      null
    );
  }, [matsOpenPlayable, selectedMatNumber]);

  const followUrl = useMemo(() => {
    return String(hub?.event?.suivi_event || "").trim();
  }, [hub?.event?.suivi_event]);

  const hasFollowUrl = useMemo(() => isValidUrl(followUrl), [followUrl]);

  return (
    <div className="w-full pt-[70px] font-satoshi bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white min-h-screen">
      <HeroImage
        title="Répartitions des Catégories & Live Video"
        subtitle="PÔLE DES ARTS MARTIAUX MÉTROPOLITAIN LORMONT - Dojo Teddy Riner"
        imageUrl="/img/cover/cover_paam.webp"
      />

      <div className="max-w-[1280px] mx-auto px-4 py-6">
        {/* Zone haute */}
        <section className="mb-6">
          <div className="rounded-3xl ring-1 ring-slate-200 dark:ring-slate-800 bg-white/90 dark:bg-slate-900/70 shadow-sm p-4 sm:p-6">
            <div className="grid gap-5 xl:grid-cols-[1.2fr_0.8fr] xl:items-end">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-200 ring-1 ring-indigo-200 dark:ring-indigo-400/20 px-3 py-1 text-xs font-bold uppercase tracking-[0.2em]">
                  <FaBroadcastTower />
                  Lives & répartitions
                </div>

                <h2 className="mt-3 text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white">
                  Compétitions disponibles
                </h2>

                <p className="mt-2 text-sm sm:text-base text-slate-600 dark:text-slate-300 max-w-3xl">
                  Consulte la répartition des tapis, les catégories en cours, les
                  flux live disponibles, ainsi que le suivi officiel de la
                  compétition lorsqu’il est renseigné.
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto]">
                <select
                  value={selectedEventId || ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (!v) return;
                    navigate(`/live-events/${v}`);
                  }}
                  className="w-full rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-indigo-400"
                >
                  <option value="" disabled>
                    {loadingEvents ? "Chargement…" : "Choisir une compétition"}
                  </option>
                  {available.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.titre} — {fmtDate(e.start)}
                    </option>
                  ))}
                </select>

                <button
                  onClick={() => {
                    if (selectedEventId) fetchHub(selectedEventId);
                  }}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 px-4 py-3 text-sm font-semibold text-slate-800 dark:text-white"
                >
                  <FaSyncAlt />
                  Rafraîchir
                </button>

                {hasFollowUrl ? (
                  <a
                    href={followUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-indigo-600 hover:bg-indigo-700 px-4 py-3 text-sm font-semibold text-white"
                  >
                    <FaQrcode />
                    Suivi compétition
                  </a>
                ) : null}
              </div>
            </div>
          </div>

          <div className="mt-4">
            {loadingEvents ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-[220px] rounded-2xl bg-slate-100 dark:bg-slate-800 animate-pulse"
                  />
                ))}
              </div>
            ) : available.length ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                {available.map((ev) => (
                  <EventCard
                    key={ev.id}
                    ev={ev}
                    isSelected={Number(ev.id) === Number(selectedEventId)}
                  />
                ))}
              </div>
            ) : (
              <div className="rounded-2xl ring-1 ring-slate-200 dark:ring-slate-800 bg-white dark:bg-slate-900/70 p-6 text-slate-700 dark:text-slate-300">
                Aucune compétition n’a pour le moment de répartition tapis
                active et/ou de liens live configurés.
              </div>
            )}
          </div>
        </section>

        {/* Hub */}
        <section>
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-100 ring-1 ring-slate-200 dark:ring-slate-700 text-sm">
              {loadingHub
                ? "Chargement…"
                : `${matsOpenPlayable.length} tapis ouverts • ${matsOpenWithStream.length} avec live`}
            </div>

            {hub?.event?.titre ? (
              <div className="text-sm text-slate-600 dark:text-slate-300">
                <span className="font-semibold">{hub.event.titre}</span> •{" "}
                {fmtRange(hub.event.start, hub.event.end)} •{" "}
                {hub.event.lieu_event || "—"}
              </div>
            ) : null}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
            {/* LEFT */}
            <div className="xl:col-span-7">
              <div className="rounded-2xl ring-1 ring-slate-200 dark:ring-slate-800 bg-white dark:bg-slate-900/70 p-4 shadow-sm">
                <div className="font-semibold text-slate-900 dark:text-white mb-3">
                  Tapis ouverts
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-3 gap-3">
                  {loadingHub ? (
                    Array.from({ length: 8 }).map((_, i) => (
                      <div
                        key={i}
                        className="h-[140px] rounded-2xl bg-slate-100 dark:bg-slate-800 animate-pulse"
                      />
                    ))
                  ) : matsOpenPlayable.length ? (
                    matsOpenPlayable.slice(0, 10).map((m) => (
                      <MatCard
                        key={m.id || m.mat_number}
                        mat={m}
                        isSelected={
                          Number(m.mat_number) === Number(selectedMatNumber)
                        }
                        onSelect={(mat) => setSelectedMatNumber(mat.mat_number)}
                      />
                    ))
                  ) : (
                    <div className="text-sm text-slate-600 dark:text-slate-300">
                      Aucun tapis ouvert n’est actuellement disponible pour cette
                      compétition.
                    </div>
                  )}
                </div>

                <div className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                  Seuls les tapis ouverts sont affichés ici.
                </div>
              </div>
            </div>

            {/* RIGHT */}
            <div className="xl:col-span-5 space-y-4">
              {hasFollowUrl ? <FollowCompetitionCard url={followUrl} /> : null}

              <div className="rounded-2xl ring-1 ring-slate-200 dark:ring-slate-800 bg-white dark:bg-slate-900/70 p-4 shadow-sm">
                <div className="mb-3">
                  <div className="text-lg font-semibold truncate text-slate-900 dark:text-white">
                    {selectedMat?.label ||
                      (selectedMat?.mat_number
                        ? `Tapis ${selectedMat.mat_number}`
                        : "Live")}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    {selectedMat?.stream?.title || "Diffusion en direct"}
                  </div>
                </div>

                <Player mat={selectedMat} />

                {selectedMat?.stream?.stream_url ? (
                  <a
                    href={selectedMat.stream.stream_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-2 text-sm text-indigo-600 dark:text-indigo-300 hover:underline"
                  >
                    Ouvrir le lien direct{" "}
                    <FaExternalLinkAlt className="text-[12px]" />
                  </a>
                ) : null}
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}